import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Parse, initParse } from "../lib/parse";
import { bootstrapLegacyStorage, getSessionToken, persistExtendedUser } from "../lib/auth";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Modal from "../components/ui/Modal";

const signerContextStorageKey = (docId) => `danasign.signer-context:${docId || ""}`;

const persistSignerContext = (docId, context = {}) => {
  if (!docId || typeof window === "undefined" || !window.sessionStorage) {
    return;
  }
  try {
    const key = signerContextStorageKey(docId);
    const current = JSON.parse(window.sessionStorage.getItem(key) || "{}");
    const next = {
      ...current,
      ...context
    };
    window.sessionStorage.setItem(key, JSON.stringify(next));
  } catch (error) {
    // noop
  }
};

function GuestLoginPage() {
  const { t, i18n } = useTranslation();
  const { id, userMail, contactBookId, base64url } = useParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState(userMail?.toLowerCase()?.replace(/\s/g, "") || "");
  const [otp, setOtp] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [documentId, setDocumentId] = useState(id || "");
  const [contactId, setContactId] = useState(contactBookId || "");
  const [sendmail, setSendmail] = useState("");
  const basePath = "";

  const isServerDownError = (err) => {
    if (!err) return false;
    const message = err.message || err.toString?.() || "";
    return (
      err.code === Parse.Error.CONNECTION_FAILED ||
      message.includes("XMLHttpRequest failed") ||
      message.includes("Network request failed") ||
      message.includes("Failed to fetch") ||
      message.includes("Network Error")
    );
  };

  const parseLinkPayload = () => {
    if (base64url && !id) {
      const decoded = atob(base64url);
      const parts = decoded.split("/");
      return {
        docId: parts[0] || "",
        emailAddress: parts[1]?.toLowerCase()?.replace(/\s/g, "") || "",
        contactIdValue: parts[2] || "",
        sendmailValue: parts[3] || ""
      };
    }

    return {
      docId: id || "",
      emailAddress: userMail?.toLowerCase()?.replace(/\s/g, "") || "",
      contactIdValue: contactBookId || "",
      sendmailValue: ""
    };
  };

  useEffect(() => {
    initParse();
    handleInitialLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInitialLoad = async () => {
    try {
      const { docId, emailAddress, contactIdValue, sendmailValue } = parseLinkPayload();
      const normalizedLinkEmail = String(emailAddress || "").trim().toLowerCase();
      const currentParseUser = Parse.User.current();
      const currentSessionToken = String(getSessionToken() || "").trim();
      const currentUserEmail = String(
        currentParseUser?.get?.("email") ||
          currentParseUser?.get?.("Email") ||
          currentParseUser?.attributes?.email ||
          currentParseUser?.attributes?.Email ||
          currentParseUser?.email ||
          localStorage.getItem("userEmail") ||
          ""
      )
        .trim()
        .toLowerCase();
      const canUseExistingSession = Boolean(
        docId &&
          currentSessionToken &&
          currentParseUser &&
          (!normalizedLinkEmail || (currentUserEmail && currentUserEmail === normalizedLinkEmail))
      );

      setDocumentId(docId);
      setEmail(emailAddress || "");
      setContactId(contactIdValue || "");
      setSendmail(sendmailValue || "");

      // If signer is already authenticated in platform, go directly to internal signing flow.
      if (canUseExistingSession) {
        persistSignerContext(docId, {
          contactBookId: contactIdValue || "",
          email: emailAddress || ""
        });
        const query = new URLSearchParams();
        if (sendmailValue === "false") {
          query.set("sendmail", "false");
        }
        const queryString = query.toString();
        navigate(`${basePath}/pdfRequestFiles/${docId}${queryString ? `?${queryString}` : ""}`, {
          replace: true
        });
        return;
      }

      // Guest flow bootstrap
      const languageBeforeClear = localStorage.getItem("i18nextLng") || "";
      localStorage.clear();
      bootstrapLegacyStorage();
      localStorage.setItem("appname", "DANAsign");
      localStorage.setItem("isGuestSigner", "true");
      if (languageBeforeClear) {
        localStorage.setItem("i18nextLng", languageBeforeClear);
      }

      if (docId && contactIdValue) {
        await navigateToDocument(docId, contactIdValue, sendmailValue);
      } else if (docId && emailAddress) {
        // Link contact to document
        await linkContactToDocument(docId, emailAddress, sendmailValue);
      } else if (docId) {
        await navigateToDocument(docId, "", sendmailValue);
      }
    } catch (err) {
      console.error("Error in initial load", err);
      setError(t("something-went-wrong-mssg", { defaultValue: "Something went wrong" }));
    } finally {
      setIsInitialLoading(false);
    }
  };

  const linkContactToDocument = async (docId, emailAddress, sendmailParam) => {
    try {
      setLoading(true);
      const result = await Parse.Cloud.run("linkcontacttodoc", {
        email: emailAddress,
        docId: docId
      });

      if (result?.contactId) {
        setContactId(result.contactId);
        await navigateToDocument(docId, result.contactId, sendmailParam);
      }
    } catch (err) {
      console.error("Error linking contact", err);
      setError(t("something-went-wrong-mssg", { defaultValue: "Something went wrong" }));
      setIsInitialLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const navigateToDocument = async (docId, contactIdParam, sendmailParam) => {
    try {
      // Check if document requires OTP
      const docDetails = await Parse.Cloud.run("getDocument", {
        docId: docId
      });

      if (docDetails?.error === "otp_required") {
        setIsInitialLoading(false);
        return;
      }

      if (!docDetails || docDetails.error) {
        if (docDetails?.error === "document_archived") {
          setError(
            t("document-archived-unavailable", {
              defaultValue:
                "Este documento fue archivado y ya no está disponible. Comuníquese con el emisor del documento."
            })
          );
        } else {
          setError(t("document-not-found", { defaultValue: "Document not found" }));
        }
        setIsInitialLoading(false);
        return;
      }

      // Navigate to external signer page
      const sendmailQuery = sendmailParam === "false" ? "?sendmail=false" : "";
      
      if (contactIdParam) {
        navigate(`${basePath}/load/recipientSignPdf/${docId}/${contactIdParam}${sendmailQuery}`);
      } else {
        navigate(`${basePath}/recipientSignPdf/${docId}${sendmailQuery}`);
      }
    } catch (err) {
      console.error("Error navigating to document", err);
      setError(t("something-went-wrong-mssg", { defaultValue: "Something went wrong" }));
      setIsInitialLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    
    if (!email) {
      setError(t("email-required", { defaultValue: "Email is required" }));
      return;
    }

    try {
      setLoading(true);
      setError("");

      await Parse.Cloud.run("SendOTPMailV1", {
        email: email.toLowerCase().replace(/\s/g, ""),
        docId: documentId,
        language: i18n.language ? i18n.language.split("-")[0] : "en"
      });

      setShowOtpModal(true);
    } catch (err) {
      console.error("Error sending OTP", err);
      if (isServerDownError(err)) {
        setError(
          t("server-down", {
            appName: "DANAsign",
            defaultValue: "The DANAsign server is temporarily unavailable."
          })
        );
      } else {
        setError(t("something-went-wrong-mssg", { defaultValue: "Something went wrong" }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (!otp) {
      setError(t("enter-otp-alert", { defaultValue: "Please enter OTP" }));
      return;
    }

    try {
      setLoading(true);
      setError("");

      if (!localStorage.getItem("baseUrl") || !localStorage.getItem("parseAppId")) {
        bootstrapLegacyStorage();
      }
      const baseUrl = String(localStorage.getItem("baseUrl") || "").trim();
      const parseAppId = String(localStorage.getItem("parseAppId") || Parse.applicationId || "").trim();
      const authUrl = `${baseUrl.replace(/\/?$/, "/")}functions/AuthLoginAsMail`;
      const normalizedEmail = email.toLowerCase().replace(/\s/g, "");
      const response = await axios.post(
        authUrl,
        { email: normalizedEmail, otp: otp },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Parse-Application-Id": parseAppId
          }
        }
      );

      const data = response?.data || {};

      if (data.result === "Invalid Otp") {
        setError(t("invalid-otp", { defaultValue: "Invalid OTP" }));
        return;
      }

      if (data.result === "user not found!") {
        setError(t("user-not-found", { defaultValue: "User not found" }));
        return;
      }

      const user = data.result;
      
      // Store user session
      await Parse.User.become(user.sessionToken);
      localStorage.setItem("accesstoken", user.sessionToken);
      localStorage.setItem("UserInformation", JSON.stringify(user));
      localStorage.setItem(`Parse/${parseAppId}/currentUser`, JSON.stringify(user));
      localStorage.setItem("userEmail", user?.email || normalizedEmail);
      localStorage.setItem("username", user?.name || user?.username || "");

      try {
        const extUser = await Parse.Cloud.run("getUserDetails");
        if (extUser) {
          persistExtendedUser(extUser);
        }
      } catch (extErr) {
        console.warn("Guest OTP login could not load extended user details", extErr);
      }

      // Navigate to document
      const sendmailQuery = sendmail === "false" ? "?sendmail=false" : "";
      if (contactId) {
        navigate(`${basePath}/load/recipientSignPdf/${documentId}/${contactId}${sendmailQuery}`);
      } else {
        navigate(`${basePath}/recipientSignPdf/${documentId}${sendmailQuery}`);
      }
    } catch (err) {
      console.error("Error verifying OTP", err);
      if (isServerDownError(err)) {
        setError(
          t("server-down", {
            appName: "DANAsign",
            defaultValue: "The DANAsign server is temporarily unavailable."
          })
        );
      } else {
        setError(t("something-went-wrong-mssg", { defaultValue: "Something went wrong" }));
      }
    } finally {
      setLoading(false);
    }
  };

  if (isInitialLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app-sand">
        <Card className="w-full max-w-md">
          <div className="flex flex-col items-center gap-4 py-8">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-app-brand border-t-transparent"></div>
            <p className="text-sm text-app-ink-muted">
              {t("loading", { defaultValue: "Loading..." })}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (error && !showOtpModal) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app-sand px-4">
        <Card className="w-full max-w-md">
          <div className="flex flex-col items-center gap-4 py-8">
            <h2 className="text-lg font-semibold text-app-ink">
              {t("error", { defaultValue: "Error" })}
            </h2>
            <p className="text-center text-sm text-app-ink-muted">{error}</p>
            <Button onClick={() => window.location.reload()}>
              {t("try-again", { defaultValue: "Try again" })}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-app-sand px-4">
      <Card className="w-full max-w-md">
        <div className="p-6">
          <h1 className="text-2xl font-semibold text-app-ink mb-6">
            {t("welcome", { defaultValue: "Welcome" })}
          </h1>

          {contactId ? (
            <div>
              <p className="text-sm text-app-ink-muted mb-4">
                {t("get-otp-alert", { defaultValue: "Get verification code to continue" })}
              </p>
              
              <div className="mb-4">
                <Input
                  type="email"
                  value={email}
                  disabled
                  className="w-full"
                />
              </div>

              <Button
                onClick={handleSendOtp}
                disabled={loading}
                className="w-full"
              >
                {loading ? t("loading", { defaultValue: "Loading..." }) : t("get-verification-code", { defaultValue: "Get verification code" })}
              </Button>
            </div>
          ) : (
            <div>
              <p className="text-sm text-app-ink-muted mb-4">
                {t("provide-your-details", { defaultValue: "Please provide your details" })}
              </p>
              <p className="text-sm text-app-ink-muted">
                {t("contact-admin", { defaultValue: "Please contact the administrator" })}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* OTP Modal */}
      <Modal
        isOpen={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        title={t("otp-verification", { defaultValue: "OTP Verification" })}
      >
        <form onSubmit={handleVerifyOtp} className="p-6">
          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-app-ink">
              {t("enter-otp", { defaultValue: "Enter OTP" })}
            </label>
            <Input
              type="text"
              pattern="[0-9]{4}"
              maxLength={4}
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder={t("otp-placeholder", { defaultValue: "Enter 4-digit code" })}
              required
              className="w-full"
            />
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? t("loading", { defaultValue: "Loading..." }) : t("verify", { defaultValue: "Verify" })}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleSendOtp}
              disabled={loading}
            >
              {t("resend", { defaultValue: "Resend" })}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default GuestLoginPage;
